import { useState, useEffect, useRef } from "react";

interface UseRoomDbWebSocketProps {
  uri: string;
  devId: string;
}

interface FileInfo {
  filename: string;
  size: number;
  chunks: number;
}

// Helper function to check if a string is Base64 encoded
const isBase64String = (str: string): boolean => {
  // Check if string is empty or too short
  if (!str || str.length < 16) return false;

  // Base64 regex pattern - only A-Z, a-z, 0-9, +, /, and = for padding
  const base64Regex = /^[A-Za-z0-9+/]+=*$/;
  if (!base64Regex.test(str)) return false;

  // Check if it starts with known SQLite Base64 signature
  // "SQLite format 3" in Base64 starts with "U1FMaXRl"
  if (str.startsWith("U1FMaXRl")) return true;

  // Additional heuristic: Base64 strings are typically long and have high entropy
  return str.length > 100;
};

// Helper function to decode Base64 to Uint8Array
const base64ToUint8Array = (base64: string): Uint8Array => {
  // Decode base64 to binary string using atob()
  const binaryString = atob(base64);

  // Convert binary string to Uint8Array
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
};

export const useRoomDbWebSocket = ({ uri, devId }: UseRoomDbWebSocketProps) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [dbFile, setDbFile] = useState<Uint8Array | null>(null);
  const [error, setError] = useState<string | null>(null);

  const chunksReceived = useRef<Uint8Array[]>([]);
  const keepAliveInterval = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (socket) {
        socket.close();
      }
      if (keepAliveInterval.current) {
        clearInterval(keepAliveInterval.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [socket]);

  // Helper function to reconstruct database from received chunks
  const reconstructDatabase = () => {
    if (chunksReceived.current.length === 0) {
      console.log("No chunks to reconstruct");
      return;
    }

    console.log(
      `Reconstructing database from ${chunksReceived.current.length} chunks`,
    );
    setLoadingMessage("Processing database...");

    // Log chunk information for debugging
    chunksReceived.current.forEach((chunk, idx) => {
      console.log(`Chunk ${idx}: ${chunk.length} bytes`);
    });

    // Reconstruct file from chunks
    const totalSize = chunksReceived.current.reduce(
      (sum, chunk) => sum + chunk.length,
      0,
    );
    console.log(`Total database size: ${totalSize} bytes`);

    const fileData = new Uint8Array(totalSize);
    let offset = 0;

    for (const chunk of chunksReceived.current) {
      fileData.set(chunk, offset);
      offset += chunk.length;
    }

    // Validate SQLite header (first 16 bytes should be "SQLite format 3\0")
    const header = fileData.slice(0, 16);
    const headerStr = String.fromCharCode(...Array.from(header));
    console.log("Database header:", headerStr);
    console.log(
      "First 16 bytes (hex):",
      Array.from(header)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(" "),
    );

    // Check if header matches SQLite signature
    const expectedHeader = [
      0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66, 0x6f, 0x72, 0x6d, 0x61,
      0x74, 0x20, 0x33, 0x00,
    ];
    const headerValid = expectedHeader.every(
      (byte, idx) => header[idx] === byte,
    );
    console.log("SQLite header valid:", headerValid);

    if (!headerValid) {
      console.error("Invalid SQLite header - database may be corrupted");
      setError("Database file appears to be corrupted (invalid header)");
      setLoadingMessage(null);
      return;
    }

    setDbFile(fileData);
    setProgress(100);
    setLoadingMessage(null);

    // Clear inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const start = () => {
    if (!uri || !devId) return;

    setLoadingMessage("Connecting to device...");
    setError(null);
    setProgress(0);
    chunksReceived.current = [];

    const ws = new WebSocket(uri);
    ws.binaryType = "arraybuffer";
    setSocket(ws);

    // Set 2-minute timeout
    timeoutRef.current = setTimeout(() => {
      setError("Device not responding (timeout)");
      setLoadingMessage(null);
      ws.close();
    }, 120000); // 2 minutes

    ws.onopen = () => {
      setLoadingMessage("Waiting for device response...");
      setIsConnected(true);
      ws.send(JSON.stringify({ admin: devId }));
    };

    ws.onmessage = (msg) => {
      // Clear timeout on any message
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Check for binary data FIRST (before string check)
      if (msg.data instanceof ArrayBuffer) {
        // Binary chunk
        const chunk = new Uint8Array(msg.data);
        chunksReceived.current.push(chunk);

        if (fileInfo) {
          const currentProgress = Math.round(
            (chunksReceived.current.length / fileInfo.chunks) * 100,
          );
          setProgress(currentProgress);
        }

        // Start/reset 10-second inactivity timer
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        inactivityTimerRef.current = setTimeout(() => {
          console.log("No new chunks for 10 seconds - auto-completing");
          reconstructDatabase();
        }, 10000);
      } else if (typeof msg.data === "string") {
        // Handle plain "ready" string first (before JSON parsing)
        if (msg.data === "ready") {
          console.log("Device is ready");
          setIsReady(true);
          setLoadingMessage("Device ready, waiting for file...");
          return;
        }

        // Handle "finish" message (sent by client or echoed by device)
        if (msg.data === "finish") {
          console.log("Received finish message");
          return;
        }

        // Check if this is a raw Base64-encoded chunk (not wrapped in JSON)
        if (isBase64String(msg.data)) {
          console.log("Received raw Base64 chunk, decoding...");
          const chunk = base64ToUint8Array(msg.data);
          chunksReceived.current.push(chunk);

          if (fileInfo) {
            const currentProgress = Math.round(
              (chunksReceived.current.length / fileInfo.chunks) * 100,
            );
            setProgress(currentProgress);
          }

          // Start/reset 10-second inactivity timer
          if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
          }
          inactivityTimerRef.current = setTimeout(() => {
            console.log("No new chunks for 10 seconds - auto-completing");
            reconstructDatabase();
          }, 10000);

          return; // Don't try to parse as JSON
        }

        // Try to parse as JSON for other string messages
        try {
          console.log("Received message:", msg.data);
          const json = JSON.parse(msg.data);

          if (json.type === "file_info") {
            setFileInfo({
              filename: json.filename,
              size: json.size,
              chunks: json.chunks,
            });
            setLoadingMessage(`Receiving database file...`);
            setIsReady(true);

            // Start keep-alive
            keepAliveInterval.current = setInterval(() => {
              ws.send("keep-alive");
            }, 60000);
          } else if (json.type === "chunk" && json.data) {
            // Base64-encoded chunk
            console.log("Received Base64 chunk, decoding...");
            const chunk = base64ToUint8Array(json.data);
            chunksReceived.current.push(chunk);

            if (fileInfo) {
              const currentProgress = Math.round(
                (chunksReceived.current.length / fileInfo.chunks) * 100,
              );
              setProgress(currentProgress);
            }

            // Start/reset 10-second inactivity timer
            if (inactivityTimerRef.current) {
              clearTimeout(inactivityTimerRef.current);
            }
            inactivityTimerRef.current = setTimeout(() => {
              console.log("No new chunks for 10 seconds - auto-completing");
              reconstructDatabase();
            }, 10000);
          } else if (json.type === "file_complete") {
            setLoadingMessage("Processing database...");

            // Reconstruct file from chunks
            const totalSize = chunksReceived.current.reduce(
              (sum, chunk) => sum + chunk.length,
              0,
            );
            const fileData = new Uint8Array(totalSize);
            let offset = 0;

            for (const chunk of chunksReceived.current) {
              fileData.set(chunk, offset);
              offset += chunk.length;
            }

            setDbFile(fileData);
            setProgress(100);
            setLoadingMessage(null);
          } else if (json.type === "error") {
            setError(json.message || "Unknown error occurred");
            setLoadingMessage(null);
          }
        } catch (e) {
          console.error("Failed to parse WebSocket message:", e);
        }
      }
    };

    ws.onerror = (event) => {
      console.error("WebSocket error:", event);
      setError("Connection error occurred");
      setLoadingMessage(null);
    };

    ws.onclose = () => {
      setIsConnected(false);
      setSocket(null);
      if (keepAliveInterval.current) {
        clearInterval(keepAliveInterval.current);
        keepAliveInterval.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  };

  const stop = () => {
    if (socket) {
      socket.send("finish");
      socket.close();
    }
    setSocket(null);
    setIsConnected(false);
    setIsReady(false);
    setDbFile(null);
    setFileInfo(null);
    setProgress(0);
    chunksReceived.current = [];

    if (keepAliveInterval.current) {
      clearInterval(keepAliveInterval.current);
      keepAliveInterval.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  return {
    start,
    stop,
    isConnected,
    isReady,
    loadingMessage,
    progress,
    fileInfo,
    dbFile,
    error,
  };
};
